/**
 * Permission Definitions for Multi-Tenant System
 * Granular permissions mapped to roles
 */

import { ROLES, ROLE_HIERARCHY } from './roles';

// Permission definitions
export const PERMISSIONS = {
  // Platform permissions (ServiceHive admin)
  PLATFORM_VIEW: 'platform:view',
  PLATFORM_MANAGE: 'platform:manage',
  TENANTS_VIEW: 'tenants:view',
  TENANTS_CREATE: 'tenants:create',
  TENANTS_EDIT: 'tenants:edit',
  TENANTS_DELETE: 'tenants:delete',
  PLATFORM_BILLING: 'platform:billing',
  PLATFORM_ANALYTICS: 'platform:analytics',
  
  // User management
  USERS_VIEW: 'users:view',
  USERS_CREATE: 'users:create',
  USERS_EDIT: 'users:edit',
  USERS_DELETE: 'users:delete',
  USERS_INVITE: 'users:invite',
  
  // Pricing
  PRICING_VIEW: 'pricing:view',
  PRICING_EDIT: 'pricing:edit',
  PRICING_APPROVE: 'pricing:approve',
  PRICING_CONSTRAINTS: 'pricing:constraints',
  
  // Trips
  TRIPS_VIEW: 'trips:view',
  TRIPS_REGISTER: 'trips:register',
  TRIPS_DEREGISTER: 'trips:deregister',
  
  // Analytics
  ANALYTICS_VIEW: 'analytics:view',
  ANALYTICS_EXPORT: 'analytics:export',
  
  // Billing
  BILLING_VIEW: 'billing:view',
  BILLING_MANAGE: 'billing:manage',
  BILLING_TOPUP: 'billing:topup',
  
  // Settings
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
  API_KEYS_VIEW: 'apikeys:view',
  API_KEYS_MANAGE: 'apikeys:manage',
  WEBHOOKS_MANAGE: 'webhooks:manage',
};

// Role to permissions mapping
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: Object.values(PERMISSIONS), // All permissions
  
  [ROLES.PLATFORM_ADMIN]: [
    PERMISSIONS.PLATFORM_VIEW,
    PERMISSIONS.TENANTS_VIEW,
    PERMISSIONS.TENANTS_EDIT,
    PERMISSIONS.PLATFORM_ANALYTICS,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.PRICING_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.BILLING_VIEW,
  ],
  
  [ROLES.PLATFORM_SUPPORT]: [
    PERMISSIONS.PLATFORM_VIEW,
    PERMISSIONS.TENANTS_VIEW,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.PRICING_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
  ],
  
  [ROLES.TENANT_OWNER]: [
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.USERS_INVITE,
    PERMISSIONS.PRICING_VIEW,
    PERMISSIONS.PRICING_EDIT,
    PERMISSIONS.PRICING_APPROVE,
    PERMISSIONS.PRICING_CONSTRAINTS,
    PERMISSIONS.TRIPS_VIEW,
    PERMISSIONS.TRIPS_REGISTER,
    PERMISSIONS.TRIPS_DEREGISTER,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.ANALYTICS_EXPORT,
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.BILLING_MANAGE,
    PERMISSIONS.BILLING_TOPUP,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.API_KEYS_VIEW,
    PERMISSIONS.API_KEYS_MANAGE,
    PERMISSIONS.WEBHOOKS_MANAGE,
  ],
  
  [ROLES.TENANT_ADMIN]: [
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_EDIT,
    PERMISSIONS.USERS_INVITE,
    PERMISSIONS.PRICING_VIEW,
    PERMISSIONS.PRICING_EDIT,
    PERMISSIONS.PRICING_APPROVE,
    PERMISSIONS.PRICING_CONSTRAINTS,
    PERMISSIONS.TRIPS_VIEW,
    PERMISSIONS.TRIPS_REGISTER,
    PERMISSIONS.TRIPS_DEREGISTER,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.ANALYTICS_EXPORT,
    PERMISSIONS.BILLING_VIEW,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.API_KEYS_VIEW,
    PERMISSIONS.WEBHOOKS_MANAGE,
  ],
  
  [ROLES.TENANT_OPS]: [
    PERMISSIONS.PRICING_VIEW,
    PERMISSIONS.PRICING_EDIT,
    PERMISSIONS.TRIPS_VIEW,
    PERMISSIONS.TRIPS_REGISTER,
    PERMISSIONS.TRIPS_DEREGISTER,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.ANALYTICS_EXPORT,
    PERMISSIONS.SETTINGS_VIEW,
  ],
  
  [ROLES.TENANT_USER]: [
    PERMISSIONS.PRICING_VIEW,
    PERMISSIONS.TRIPS_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.SETTINGS_VIEW,
  ],
  
  [ROLES.TENANT_VIEWER]: [
    PERMISSIONS.PRICING_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
  ],
};

// Check if a role has a specific permission
export const hasPermission = (role, permission) => {
  const rolePerms = ROLE_PERMISSIONS[role] || [];
  return rolePerms.includes(permission);
};

// Check if role can access another role (for user management)
export const canManageRole = (userRole, targetRole) => {
  return ROLE_HIERARCHY[userRole] > ROLE_HIERARCHY[targetRole];
};

// Get all permissions for a role
export const getPermissionsForRole = (role) => ROLE_PERMISSIONS[role] || [];

export default PERMISSIONS;

